import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { SessionService } from '../services/session/session.service';
import { API_ENDPOINTS } from '../../shared/constants/endpoints';

const EXCLUDED_URL_FRAGMENTS = [
  API_ENDPOINTS.Authentication.login(),
  API_ENDPOINTS.Authentication.refresh(),
  API_ENDPOINTS.Authentication.logout(),
  API_ENDPOINTS.Authentication.validateAuth(),
  API_ENDPOINTS.User.CheckData(),
];

/**
 * Catches a 401 from any non-auth request, refreshes the session via SessionService (shared
 * with the idle-warning dialog and the proactive-refresh timer, so they never race each other),
 * and retries the original request once. Without this, a client-side timer drifting past the
 * access token's real expiry (backgrounded tab, laptop sleep/wake) surfaces a raw 401 straight
 * to the UI instead of recovering silently.
 */
export const authRefreshInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const sessionService = inject(SessionService);

  if (EXCLUDED_URL_FRAGMENTS.some((fragment) => req.url.includes(fragment))) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      return sessionService
        .ensureValidSession()
        .pipe(
          switchMap((refreshed) =>
            refreshed ? next(req) : throwError(() => error),
          ),
        );
    }),
  );
};
